import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  suggestChannelNames,
  checkChannelName,
  generateChannelGraphics,
  getChannelGraphics,
  cancelChannelGraphics,
  channelGraphicsDownloadUrl,
  listChannelCreations,
  deleteChannelCreation,
  editChannelCreation,
  regenerateChannelGraphics,
} from "@/lib/api";
import { BannerSafeZonePreview } from "@/components/BannerSafeZonePreview";
import { listCharacters, characterImageUrl } from "@/lib/charactersApi";
import RegenerateChannelKitDialog from "@/components/RegenerateChannelKitDialog";
import SaveKitButton from "@/components/SaveKitButton";
import SendToYouTubeDialog from "@/components/SendToYouTubeDialog";
import StylePresetPicker from "@/components/StylePresetPicker";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: "topic", label: "Topic" },
  { key: "name", label: "Name" },
  { key: "check", label: "Availability" },
  { key: "graphics", label: "Graphics" },
  { key: "download", label: "Download" },
];

/** Assemble the paste-ready plain-text starter-kit for a completed
 * channel creation. Mirrors backend `format_starter_kit_text` so what
 * ships in the ZIP matches what the "⧉ Copy full kit" button writes
 * to the clipboard. Fields degrade gracefully if the LLM starter-kit
 * generation didn't populate a slot. */
function formatStarterKitText(c) {
  const lines = [];
  lines.push("YT Maker · Channel Starter Kit");
  lines.push("─".repeat(40));
  lines.push(`Name       ${c.name || ""}`);
  if (c.handle) lines.push(`Handle     ${c.handle}`);
  if (c.topic) lines.push(`Topic      ${c.topic}`);
  if (c.tagline) {
    lines.push("");
    lines.push(`Tagline    ${c.tagline}`);
  }
  if (c.description) {
    lines.push("");
    lines.push("Channel description (paste into YouTube 'Description'):");
    lines.push("");
    lines.push(String(c.description).trim());
  }
  if (Array.isArray(c.keywords) && c.keywords.length) {
    lines.push("");
    lines.push("Keywords / tags (paste into YouTube 'Keywords'):");
    lines.push(c.keywords.filter((k) => String(k).trim()).join(", "));
  }
  const palette = c.color_palette || {};
  const slots = ["primary", "secondary", "accent", "background"];
  const hasPalette = slots.some((s) => palette[s]);
  if (hasPalette) {
    lines.push("");
    lines.push("Color palette:");
    slots.forEach((s) => {
      if (palette[s]) {
        lines.push(`  ${s.charAt(0).toUpperCase() + s.slice(1).padEnd(10)} ${palette[s]}`);
      }
    });
    if (palette.reasoning) {
      lines.push(`  Rationale   ${palette.reasoning}`);
    }
  }
  lines.push("");
  return lines.join("\n");
}

function NameAvailabilityBadge({ availability }) {
  if (!availability) {
    return (
      <span
        data-testid="cc-name-availability-unknown"
        title="YouTube availability check skipped (no API key) — verify manually before claiming."
        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[2px] rounded-sm border border-zinc-700 text-zinc-500 shrink-0"
      >
        ?
      </span>
    );
  }
  if (availability.exact_match) {
    return (
      <span
        data-testid="cc-name-availability-taken"
        title="An existing YouTube channel has this exact name — handle will need to differ, and the brand will compete with theirs."
        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[2px] rounded-sm border border-red-500/50 text-red-300 bg-red-500/10 shrink-0"
      >
        ✗ taken
      </span>
    );
  }
  if (availability.match_count >= 5) {
    return (
      <span
        data-testid="cc-name-availability-crowded"
        title={`${availability.match_count} channels on YouTube look similar — no exact match, but you'll compete for search visibility.`}
        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[2px] rounded-sm border border-yellow-500/50 text-yellow-300 bg-yellow-500/10 shrink-0"
      >
        ⚠ crowded
      </span>
    );
  }
  return (
    <span
      data-testid="cc-name-availability-clear"
      title={`No exact match on YouTube. ${availability.match_count} loosely similar channels found.`}
      className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[2px] rounded-sm border border-emerald-500/50 text-emerald-300 bg-emerald-500/10 shrink-0"
    >
      ✓ clear
    </span>
  );
}


function StepRail({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-3 mb-10 font-mono text-[11px] uppercase tracking-[0.25em]">
      {STEPS.map((s, i) => {
        const state = i < idx ? "done" : i === idx ? "active" : "todo";
        return (
          <li key={s.key} className="flex items-center gap-3">
            <span
              className={`h-6 w-6 grid place-items-center rounded-sm border ${
                state === "done"
                  ? "border-yellow-400 bg-yellow-400 text-black"
                  : state === "active"
                  ? "border-yellow-400 text-yellow-400"
                  : "border-zinc-800 text-zinc-600"
              }`}
            >
              {i + 1}
            </span>
            <span className={state === "todo" ? "text-zinc-600" : "text-zinc-300"}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="text-zinc-800 mx-1">/</span>}
          </li>
        );
      })}
    </ol>
  );
}

function SectionHeading({ kicker, title, subtitle }) {
  return (
    <div className="mb-8">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-2">
        {kicker}
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-black text-white mb-3">
        {title}
      </h2>
      {subtitle && <p className="text-zinc-400 text-sm max-w-2xl">{subtitle}</p>}
    </div>
  );
}

export default function CreateChannel() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("topic");

  // Step 1: topic — pre-fill from URL params when the user lands here
  // from a Saved Niche → "Create channel from this niche" flow.
  const [topic, setTopic] = useState(searchParams.get("topic") || "");
  const [styleHint, setStyleHint] = useState(searchParams.get("style") || "");
  const [stylePreset, setStylePreset] = useState("pixar_3d");
  // Banner image provider — `"openai"` (gpt-image-1, CGI/Pixar-leaning)
  // or `"gemini"` (Nano Banana, native 16:9 output). Persisted with
  // the kit so Regenerate uses the same choice by default.
  const [bannerProvider, setBannerProvider] = useState("openai");
  // Character library — multi-select. When non-empty, the user's
  // chosen cast is injected into the avatar+banner prompts so the
  // same Bella/Benny/etc render consistently.
  const [characters, setCharacters] = useState([]);
  const [pickedCharacterIds, setPickedCharacterIds] = useState([]);
  useEffect(() => {
    listCharacters().then(setCharacters).catch(() => setCharacters([]));
  }, []);
  const toggleCharacter = (id) => {
    setPickedCharacterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const sourceNiche = searchParams.get("niche") || "";
  const sourceChannelId = searchParams.get("source_channel_id") || "";

  // "Your channels" gallery — every channel kit the user has created
  // shows up here so they can re-download or reference past work.
  const [creations, setCreations] = useState([]);
  const [loadingCreations, setLoadingCreations] = useState(true);
  const reloadCreations = useCallback(async () => {
    try {
      const { items } = await listChannelCreations();
      setCreations(items || []);
    } catch {
      /* non-fatal — gallery just stays hidden */
    } finally {
      setLoadingCreations(false);
    }
  }, []);
  useEffect(() => { reloadCreations(); }, [reloadCreations]);

  // OAuth callback lands here with ?yt_link=success or ?yt_link=error.
  // Show a toast and strip the query so a refresh doesn't re-fire it.
  useEffect(() => {
    const flag = searchParams.get("yt_link");
    if (!flag) return;
    if (flag === "success") {
      toast.success("YouTube channel linked — you can now send kits to YouTube.");
    } else if (flag === "error") {
      const reason = searchParams.get("reason") || "unknown_error";
      const detail = searchParams.get("detail");
      toast.error(
        detail ? `YouTube link failed (${reason}): ${detail}` : `YouTube link failed: ${reason}`,
        { duration: 15000 },
      );
      // eslint-disable-next-line no-console
      if (detail) console.error("[youtube-oauth] error detail:", detail);
    }
    // Clear the params without adding a history entry.
    const url = new URL(window.location.href);
    url.searchParams.delete("yt_link");
    url.searchParams.delete("reason");
    url.searchParams.delete("detail");
    window.history.replaceState({}, "", url.pathname + url.search);
  }, [searchParams]);

  const removeCreation = useCallback(async (c) => {
    const ok = window.confirm(
      `Delete "${c.name || "this kit"}" from your channels?\n\n` +
      `This permanently removes the saved kit (avatar + banners + name + handle). ` +
      `You can always create a fresh one from a saved niche. This cannot be undone.`
    );
    if (!ok) return;
    // Optimistic remove so the UI feels snappy.
    setCreations((prev) => prev.filter((x) => x.job_id !== c.job_id));
    try {
      await deleteChannelCreation(c.job_id);
      toast.success(`Removed · ${c.name || "kit"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove kit");
      // Re-fetch on failure so we don't show stale state.
      reloadCreations();
    }
  }, [reloadCreations]);

  /**
   * Re-run graphics generation for an EXISTING kit. Used when prod
   * disk purged the old PNGs (the "BANNER UNAVAILABLE" placeholder)
   * so the user can recover a broken kit in one click without
   * losing the chosen channel name / handle / topic / cast. Also
   * lets the user ATTACH characters from their Library before the
   * re-run, via the RegenerateChannelKitDialog modal.
   *
   * The backend flips the kit's status back to running, drops new
   * bytes into both disk AND GridFS, and the UI auto-polls until it
   * resolves complete or failed.
   */
  const [regenKit, setRegenKit] = useState(null);
  const [sendToYtKit, setSendToYtKit] = useState(null);

  const openRegen = useCallback((c) => setRegenKit(c), []);

  const runRegen = useCallback(async (characterIds, stylePresetKey = null, providerKey = null) => {
    if (!regenKit) return;
    const c = regenKit;
    try {
      await regenerateChannelGraphics(c.job_id, characterIds, stylePresetKey, providerKey);
      toast.success("Regenerating — this can take 30–60 seconds.");
      // Optimistically flip the row to running so the user gets
      // immediate visual feedback (animated ◌ rendering pill).
      setCreations((prev) => prev.map((x) =>
        x.job_id === c.job_id
          ? {
              ...x, status: "running", images: {}, error: undefined,
              character_ids: characterIds,
              style_preset: stylePresetKey ?? x.style_preset,
              banner_provider: providerKey ?? x.banner_provider,
            }
          : x
      ));
      // Poll for ~3 minutes then stop. The card auto-renders its
      // running state from the optimistic update above, so no
      // separate animation hook is needed here.
      let elapsed = 0;
      const iv = setInterval(async () => {
        elapsed += 4;
        if (elapsed > 180) {
          clearInterval(iv);
          reloadCreations();
          return;
        }
        try {
          const doc = await getChannelGraphics(c.job_id);
          if (doc.status === "complete" || doc.status === "failed") {
            clearInterval(iv);
            reloadCreations();
            if (doc.status === "complete") {
              toast.success(`Refreshed · ${c.name || "kit"}`);
            } else {
              toast.error(doc.error || "Regeneration failed");
            }
          }
        } catch {  /* keep polling */ }
      }, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start regeneration");
    }
  }, [regenKit, reloadCreations]);

  // Step 2: names
  const [names, setNames] = useState([]);
  const [seenNames, setSeenNames] = useState([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [pickedName, setPickedName] = useState("");
  const [pickedHandle, setPickedHandle] = useState("");

  // Step 3: uniqueness check
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  // Step 4: graphics job
  const [job, setJob] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startedAtRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
  }, []);

  // ---------- Step 1 → 2 ----------
  const startNames = async () => {
    if (topic.trim().length < 3) {
      toast.error("Describe your channel topic in at least 3 characters");
      return;
    }
    setLoadingNames(true);
    setNames([]);
    setSeenNames([]);
    try {
      const { names: list } = await suggestChannelNames(topic.trim(), [], 5);
      setNames(list);
      setSeenNames(list.map((n) => n.name));
      setStep("name");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't get name ideas");
    } finally {
      setLoadingNames(false);
    }
  };

  const loadMoreNames = async () => {
    setLoadingNames(true);
    try {
      const { names: list } = await suggestChannelNames(topic.trim(), seenNames, 5);
      if (list.length === 0) {
        toast.info("No fresh ideas — try refining the topic");
      } else {
        setNames((prev) => [...prev, ...list]);
        setSeenNames((prev) => [...prev, ...list.map((n) => n.name)]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't fetch more names");
    } finally {
      setLoadingNames(false);
    }
  };

  const pickName = (n) => {
    setPickedName(n.name);
    setPickedHandle(n.handle);
  };

  // ---------- Step 2 → 3 ----------
  const runCheck = async () => {
    if (!pickedName.trim()) {
      toast.error("Pick or type a name first");
      return;
    }
    setChecking(true);
    setCheckResult(null);
    setStep("check");
    try {
      const r = await checkChannelName(pickedName.trim());
      setCheckResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "YouTube check failed");
    } finally {
      setChecking(false);
    }
  };

  // ---------- Step 3 → 4 ----------
  const startGraphics = async () => {
    setStep("graphics");
    setJob({ status: "running" });
    setElapsed(0);
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
    startedAtRef.current = Date.now();
    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    try {
      const { job_id } = await generateChannelGraphics({
        name: pickedName.trim(),
        handle: pickedHandle.trim() || undefined,
        topic: topic.trim(),
        style_hint: styleHint.trim() || undefined,
        source_niche: sourceNiche || undefined,
        source_channel_id: sourceChannelId || undefined,
        character_ids: pickedCharacterIds.length ? pickedCharacterIds : undefined,
        style_preset: stylePreset || undefined,
        banner_provider: bannerProvider || undefined,
      });
      setJob({ status: "running", job_id });
      pollRef.current = setInterval(async () => {
        try {
          const doc = await getChannelGraphics(job_id);
          setJob(doc);
          if (doc.status === "complete" || doc.status === "failed" || doc.status === "cancelled") {
            clearInterval(pollRef.current);
            clearInterval(elapsedRef.current);
            if (doc.status === "complete") setStep("download");
            else if (doc.status === "failed") toast.error(doc.error || "Graphics generation failed");
            // Refresh the gallery so the new kit appears regardless of outcome.
            reloadCreations();
          }
        } catch (err) {
          clearInterval(pollRef.current);
          clearInterval(elapsedRef.current);
          toast.error("Lost connection to graphics job");
        }
      }, 3500);
    } catch (err) {
      clearInterval(pollRef.current);
      clearInterval(elapsedRef.current);
      setJob(null);
      setStep("check");
      toast.error(err?.response?.data?.detail || "Couldn't start graphics job");
    }
  };

  const cancelGraphics = async () => {
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
    if (job?.job_id) {
      try {
        await cancelChannelGraphics(job.job_id);
      } catch (err) {
        // Server-side cancel is best-effort — log so we can spot patterns
        // (e.g. cancel hitting a 404 because the job already finished),
        // but never block the user-facing flow on it.
        // eslint-disable-next-line no-console
        console.warn("cancelChannelGraphics failed (continuing anyway):", err);
      }
    }
    setJob(null);
    setStep("check");
    toast.info("Generation cancelled — tweak the style or name and try again");
  };

  // ---------- UI ----------
  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="create-channel-page">
      {regenKit && (
        <RegenerateChannelKitDialog
          kit={regenKit}
          onClose={() => setRegenKit(null)}
          onConfirm={runRegen}
        />
      )}
      {sendToYtKit && (
        <SendToYouTubeDialog
          kit={sendToYtKit}
          onClose={() => setSendToYtKit(null)}
          onApplied={() => reloadCreations()}
        />
      )}
      <div className="max-w-5xl mx-auto px-8 py-12">
        <div className="mb-8">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">
            // YT Maker · Channel Builder
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white">
            Create your YouTube channel.
          </h1>
          {sourceNiche && (
            <div
              data-testid="cc-from-niche-banner"
              className="mt-4 inline-flex items-center gap-3 border border-yellow-400/40 bg-yellow-400/[0.06] px-4 py-2 rounded-sm"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 font-bold">
                FROM SAVED NICHE
              </span>
              <span className="text-sm text-zinc-200">{sourceNiche}</span>
            </div>
          )}
        </div>

        <StepRail current={step} />

        {step === "topic" && (
          <YourChannelsGallery
            items={creations}
            loading={loadingCreations}
            onDelete={removeCreation}
            onRegenerate={openRegen}
            onSendToYouTube={setSendToYtKit}
            onEdited={reloadCreations}
          />
        )}

        {/* STEP 1 — Topic */}
        {step === "topic" && (
          <section data-testid="cc-step-topic">
            <SectionHeading
              kicker="01 · Topic"
              title="What is your channel about?"
              subtitle="Describe it the way you'd pitch it to a friend. The more specific the topic, the better the name and graphic ideas will be."
            />
            <textarea
              data-testid="cc-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. dark-academia history mysteries narrated over moody cinematic footage"
              className="w-full min-h-[120px] bg-zinc-950 border border-zinc-800 rounded-sm p-4 font-mono text-sm text-white focus:outline-none focus:border-yellow-400"
            />
            <input
              data-testid="cc-style-input"
              value={styleHint}
              onChange={(e) => setStyleHint(e.target.value)}
              placeholder="Optional: visual style hint (e.g. 'cinematic, navy + gold, film grain')"
              className="w-full mt-3 bg-zinc-950 border border-zinc-800 rounded-sm p-3 font-mono text-sm text-zinc-300 focus:outline-none focus:border-yellow-400"
            />
            <div className="mt-3">
              <StylePresetPicker
                value={stylePreset}
                onChange={setStylePreset}
                testId="cc-style-preset"
              />
            </div>
            <div className="mt-3" data-testid="cc-banner-provider-picker">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                Banner image model
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="cc-banner-provider-openai"
                  onClick={() => setBannerProvider("openai")}
                  className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${
                    bannerProvider === "openai"
                      ? "border-yellow-400 bg-yellow-400/10 text-white"
                      : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold">
                    OpenAI · gpt-image-1
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                    Volumetric CGI / Pixar 3D · 1536×1024 (upscaled)
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="cc-banner-provider-gemini"
                  onClick={() => setBannerProvider("gemini")}
                  className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${
                    bannerProvider === "gemini"
                      ? "border-yellow-400 bg-yellow-400/10 text-white"
                      : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold">
                    Gemini · Nano Banana
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                    Native 16:9 (no cropping) · flatter 2D-illustration feel
                  </div>
                </button>
              </div>
            </div>
            {characters.length > 0 && (
              <div
                data-testid="cc-character-picker"
                className="mt-3 border border-zinc-800 rounded-sm p-3 bg-zinc-950/40"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Cast from your character library (optional)
                  </div>
                  <Link
                    to="/characters"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 hover:text-yellow-300"
                  >
                    Manage →
                  </Link>
                </div>
                <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                  Pick the recurring characters you want featured. Their
                  descriptions + reference photos will be injected into the
                  avatar and banner prompts for consistent visuals.
                </p>
                <div className="flex flex-wrap gap-2">
                  {characters.map((c) => {
                    const picked = pickedCharacterIds.includes(c.character_id);
                    const firstImg = (c.images || [])[0];
                    return (
                      <button
                        key={c.character_id}
                        type="button"
                        onClick={() => toggleCharacter(c.character_id)}
                        data-testid={`cc-character-pill-${c.character_id}`}
                        className={
                          "flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs transition-colors " +
                          (picked
                            ? "border-yellow-400 bg-yellow-400/10 text-yellow-300"
                            : "border-zinc-700 text-zinc-300 hover:border-yellow-400/60 hover:text-yellow-400")
                        }
                      >
                        {firstImg && (
                          <img
                            src={characterImageUrl(c.character_id, firstImg.image_id)}
                            alt=""
                            className="w-5 h-5 rounded-sm object-cover"
                          />
                        )}
                        <span className="font-bold">{c.name}</span>
                        {c.role && (
                          <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                            {c.role}
                          </span>
                        )}
                        {picked && <span className="text-yellow-400 font-bold">✓</span>}
                      </button>
                    );
                  })}
                </div>
                {pickedCharacterIds.length > 0 && (
                  <div className="mt-2 font-mono text-[10px] text-zinc-600">
                    {pickedCharacterIds.length} selected — will be used for avatar + banners
                  </div>
                )}
              </div>
            )}
            <button
              data-testid="cc-start-names-btn"
              onClick={startNames}
              disabled={loadingNames}
              className="mt-6 bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-50"
            >
              {loadingNames ? "Generating ideas…" : "Suggest channel names →"}
            </button>
          </section>
        )}

        {/* STEP 2 — Names */}
        {step === "name" && (
          <section data-testid="cc-step-name">
            <SectionHeading
              kicker="02 · Name"
              title="Pick a name (or remix one)."
              subtitle="Tap a card to use it. You can also type your own custom name and handle below."
            />
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {names.map((n) => (
                <button
                  key={n.name}
                  data-testid={`cc-name-card-${n.name}`}
                  onClick={() => pickName(n)}
                  className={`text-left border p-4 rounded-sm transition-colors ${
                    pickedName === n.name
                      ? "border-yellow-400 bg-zinc-950"
                      : "border-zinc-800 hover:border-zinc-600 bg-black"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-xl font-bold text-white">{n.name}</div>
                    <NameAvailabilityBadge availability={n.availability} />
                  </div>
                  <div className="font-mono text-xs text-yellow-400 mt-1">{n.handle}</div>
                  <div className="text-xs text-zinc-500 mt-2 leading-relaxed">{n.why}</div>
                </button>
              ))}
            </div>
            <button
              data-testid="cc-more-names-btn"
              onClick={loadMoreNames}
              disabled={loadingNames}
              className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2 rounded-sm transition-colors disabled:opacity-50"
            >
              {loadingNames ? "Loading…" : "+ MORE ideas"}
            </button>

            <div className="mt-8 border-t border-zinc-900 pt-6">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Your final name
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="cc-final-name-input"
                  value={pickedName}
                  onChange={(e) => setPickedName(e.target.value)}
                  placeholder="Channel name"
                  className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 font-mono text-sm text-white focus:outline-none focus:border-yellow-400"
                />
                <input
                  data-testid="cc-final-handle-input"
                  value={pickedHandle}
                  onChange={(e) => setPickedHandle(e.target.value)}
                  placeholder="@handle"
                  className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 font-mono text-sm text-yellow-400 focus:outline-none focus:border-yellow-400"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                data-testid="cc-back-to-topic-btn"
                onClick={() => setStep("topic")}
                className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 hover:text-white px-4 py-2"
              >
                ← back
              </button>
              <button
                data-testid="cc-check-name-btn"
                onClick={runCheck}
                disabled={!pickedName.trim()}
                className="bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-50"
              >
                Check on YouTube →
              </button>
            </div>
          </section>
        )}

        {/* STEP 3 — Availability */}
        {step === "check" && (
          <section data-testid="cc-step-check">
            <SectionHeading
              kicker="03 · Availability"
              title={`Checking "${pickedName}" on YouTube…`}
              subtitle="We don't lock names (YouTube doesn't either) — but if a big channel already owns it, you'll want to know."
            />
            {checking && (
              <div className="font-mono text-sm text-zinc-500" data-testid="cc-check-loading">
                [recon] querying YouTube<span className="blink">_</span>
              </div>
            )}
            {checkResult && (
              <div data-testid="cc-check-result">
                {checkResult.exact_match ? (
                  <div className="border border-red-500/40 bg-red-500/10 p-4 rounded-sm mb-6 text-red-200">
                    <div className="font-mono text-xs uppercase tracking-[0.25em] mb-1">⚠ exact match found</div>
                    A channel called <b>{pickedName}</b> already exists on YouTube. You can still
                    use this name (YouTube allows duplicates), but consider tweaking it for
                    discoverability.
                  </div>
                ) : (
                  <div className="border border-emerald-500/40 bg-emerald-500/10 p-4 rounded-sm mb-6 text-emerald-200">
                    <div className="font-mono text-xs uppercase tracking-[0.25em] mb-1">✓ looks clear</div>
                    No channel with an exact match for <b>{pickedName}</b>. You're good to proceed.
                  </div>
                )}
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
                  Similar channels we found
                </div>
                <div className="space-y-2 mb-6">
                  {checkResult.matches.length === 0 && (
                    <div className="text-zinc-500 text-sm">No similar channels.</div>
                  )}
                  {checkResult.matches.map((m) => (
                    <a
                      key={m.channel_id}
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-start gap-3 border border-zinc-800 hover:border-zinc-600 p-3 rounded-sm"
                    >
                      {m.thumbnail && (
                        <img src={m.thumbnail} alt={m.title} className="h-10 w-10 rounded-full" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">
                          {m.title}{" "}
                          {m.exact && (
                            <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">
                              exact
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{m.description}</div>
                      </div>
                    </a>
                  ))}
                </div>
                <div className="border border-zinc-800 bg-zinc-950/40 p-4 rounded-sm mb-6">
                  <div className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 mb-3">
                    Confirm before generating graphics
                  </div>
                  <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                    These are the exact name and handle we'll bake into your avatar
                    and banner art. Tweak them if you want — graphics generation takes
                    ~90s, so it's cheaper to fix typos now.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1">
                        Channel name
                      </label>
                      <input
                        data-testid="cc-confirm-name-input"
                        value={pickedName}
                        onChange={(e) => setPickedName(e.target.value)}
                        className="w-full bg-black border border-zinc-800 rounded-sm p-3 font-mono text-sm text-white focus:outline-none focus:border-yellow-400"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1">
                        Handle
                      </label>
                      <input
                        data-testid="cc-confirm-handle-input"
                        value={pickedHandle}
                        onChange={(e) => setPickedHandle(e.target.value)}
                        className="w-full bg-black border border-zinc-800 rounded-sm p-3 font-mono text-sm text-yellow-400 focus:outline-none focus:border-yellow-400"
                      />
                    </div>
                  </div>
                  {pickedName.trim() && pickedName.trim().toLowerCase() !== (checkResult.name || "").toLowerCase() && (
                    <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400">
                      ⚠ name changed — re-check on YouTube before generating
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {pickedName.trim() && pickedName.trim().toLowerCase() !== (checkResult.name || "").toLowerCase() ? (
                    <button
                      data-testid="cc-recheck-name-btn"
                      onClick={runCheck}
                      disabled={checking || !pickedName.trim()}
                      className="bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-50"
                    >
                      {checking ? "Re-checking…" : "↻ Re-check on YouTube"}
                    </button>
                  ) : checkResult.exact_match ? (
                    <>
                      <button
                        data-testid="cc-try-another-name-btn"
                        onClick={() => {
                          setPickedName("");
                          setPickedHandle("");
                          setCheckResult(null);
                          setStep("name");
                        }}
                        className="bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors"
                      >
                        ↺ Try another name
                      </button>
                      <button
                        data-testid="cc-more-ideas-btn"
                        onClick={async () => {
                          setPickedName("");
                          setPickedHandle("");
                          setCheckResult(null);
                          setStep("name");
                          await loadMoreNames();
                        }}
                        className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors"
                      >
                        + More AI ideas
                      </button>
                      <button
                        data-testid="cc-generate-graphics-btn"
                        onClick={startGraphics}
                        disabled={!pickedName.trim()}
                        className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 hover:text-white px-4 py-3 disabled:opacity-40"
                      >
                        Use it anyway →
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        data-testid="cc-generate-graphics-btn"
                        onClick={startGraphics}
                        disabled={!pickedName.trim()}
                        className="bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-50"
                      >
                        ✓ Confirm & generate graphics →
                      </button>
                      <button
                        data-testid="cc-try-another-name-btn"
                        onClick={() => {
                          setPickedName("");
                          setPickedHandle("");
                          setCheckResult(null);
                          setStep("name");
                        }}
                        className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors"
                      >
                        ↺ Try another name
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* STEP 4 — Graphics in progress */}
        {step === "graphics" && (
          <section data-testid="cc-step-graphics">
            <SectionHeading
              kicker="04 · Graphics"
              title="Generating your channel art…"
              subtitle="We're creating one avatar and two banner concepts in parallel. This usually takes 30–90 seconds. You can leave the tab open."
            />
            <div className="border border-zinc-800 p-6 rounded-sm">
              <div className="flex items-center gap-3 font-mono text-sm text-zinc-400 mb-4">
                <span className="h-2 w-2 bg-yellow-400 rounded-full animate-pulse" />
                {job?.status === "complete" ? "complete" : "rendering"}
                <span className="text-zinc-700">/</span>
                <span className="text-zinc-500">job {job?.job_id || "…"}</span>
                <span className="text-zinc-700">/</span>
                <span className="text-zinc-500" data-testid="cc-elapsed">
                  {elapsed}s elapsed
                </span>
              </div>
              <div className="text-xs text-zinc-500 leading-relaxed mb-4">
                <div>Channel: <span className="text-white">{pickedName}</span></div>
                <div>Handle: <span className="text-yellow-400">{pickedHandle}</span></div>
                <div>Topic: <span className="text-zinc-300">{topic}</span></div>
                {styleHint && (
                  <div>Style: <span className="text-zinc-300">{styleHint}</span></div>
                )}
              </div>

              {elapsed > 60 && (
                <div className="border-t border-zinc-900 pt-4 mt-4">
                  <div className="text-xs text-zinc-400 mb-3">
                    {elapsed > 180 ? (
                      <>
                        <span className="text-red-400">⚠ Taking longer than expected.</span>{" "}
                        The model may be hung. We recommend cancelling and trying a different style.
                      </>
                    ) : (
                      <>Still working… image gen sometimes takes up to 2 minutes. You can cancel anytime.</>
                    )}
                  </div>
                  <button
                    data-testid="cc-cancel-graphics-btn"
                    onClick={cancelGraphics}
                    className={`font-mono text-xs uppercase tracking-[0.25em] px-4 py-2 rounded-sm border transition-colors ${
                      elapsed > 180
                        ? "border-red-500/60 text-red-300 hover:bg-red-500/10"
                        : "border-zinc-700 text-zinc-400 hover:border-yellow-400 hover:text-yellow-400"
                    }`}
                  >
                    ✕ Cancel & go back
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* STEP 5 — Download */}
        {step === "download" && job?.status === "complete" && (
          <section data-testid="cc-step-download">
            <SectionHeading
              kicker="05 · Download"
              title="Channel starter pack ready."
              subtitle="Preview the art below, then download the ZIP and upload them in YouTube Studio → Customization → Branding."
            />
            {/* Avatar stays compact (top-left). Banners get their
                own full-width row each — the safe-zone preview
                needs real horizontal space to make the desktop
                (2560×423) band tall enough to actually see the
                artwork. When squeezed into a 3-col grid at 350px
                wide, the desktop band shrank to ~60px tall and
                the stick figures got clipped. */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
              {job.images?.["avatar.png"] && (
                <div className="border border-zinc-800 p-3 rounded-sm sm:col-span-1">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">avatar</div>
                  <img
                    data-testid="cc-avatar-img"
                    src={job.images["avatar.png"]}
                    alt="Channel avatar"
                    className="w-full aspect-square object-cover rounded-sm bg-zinc-950"
                  />
                </div>
              )}
            </div>
            <div className="space-y-4 mb-6">
              {["banner_1.png", "banner_2.png"].map((k, i) => job.images?.[k] && (
                <div key={k} className="border border-zinc-800 p-3 rounded-sm">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                    banner {String.fromCharCode(65 + i)} · 2560×1440
                  </div>
                  <BannerSafeZonePreview
                    src={job.images[k]}
                    alt={`Banner concept ${i + 1}`}
                  />
                  <p
                    data-testid={`cc-banner-${i + 1}-img`}
                    className="hidden"
                  />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                data-testid="cc-continue-to-storyboard-btn"
                to="/storyboards"
                className="bg-yellow-400 text-black font-black uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors shadow-[0_0_0_1px_rgba(250,204,21,0.5)]"
              >
                ▸ Continue to Storyboard
              </Link>
              <a
                data-testid="cc-download-zip-btn"
                href={channelGraphicsDownloadUrl(job.job_id)}
                className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors"
              >
                ↓ Download kit (.zip)
              </a>
              <button
                data-testid="cc-regenerate-btn"
                onClick={startGraphics}
                className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors"
              >
                ↻ Regenerate graphics
              </button>
              <button
                data-testid="cc-restart-btn"
                onClick={() => {
                  clearInterval(pollRef.current);
                  setStep("topic");
                  setNames([]);
                  setSeenNames([]);
                  setPickedName("");
                  setPickedHandle("");
                  setCheckResult(null);
                  setJob(null);
                }}
                className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 hover:text-white border border-zinc-800 hover:border-zinc-600 px-4 py-2 rounded-sm"
              >
                Start another channel
              </button>
            </div>

            <div className="mt-8 border border-zinc-800 bg-zinc-950/40 p-4 rounded-sm">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
                Not happy with the look? Tweak the style + regenerate
              </div>
              <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                Try something like{" "}
                <i className="text-zinc-400">
                  "minimalist black & neon-green, vector flat shapes"
                </i>{" "}
                or{" "}
                <i className="text-zinc-400">
                  "warm sunset tones, hand-drawn illustration"
                </i>
                . Then hit ↻ Regenerate above (or below).
              </p>
              <input
                data-testid="cc-style-tweak-input"
                value={styleHint}
                onChange={(e) => setStyleHint(e.target.value)}
                placeholder="Visual style direction (color palette, mood, art style)…"
                className="w-full bg-black border border-zinc-800 rounded-sm p-3 font-mono text-sm text-zinc-200 focus:outline-none focus:border-yellow-400"
              />
              <button
                data-testid="cc-regenerate-with-style-btn"
                onClick={startGraphics}
                className="mt-3 bg-yellow-400 text-black font-semibold px-5 py-2.5 rounded-sm hover:bg-yellow-300 transition-colors"
              >
                ↻ Regenerate with this style
              </button>
            </div>
            <div className="mt-10 border-t border-zinc-900 pt-6">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Next steps in YouTube Studio
              </div>
              <ol className="space-y-2 text-sm text-zinc-300 list-decimal list-inside">
                <li>Go to studio.youtube.com → Customization → Branding</li>
                <li>Upload <span className="text-yellow-400">avatar.png</span> as your channel picture</li>
                <li>Upload one of the <span className="text-yellow-400">banner_*.png</span> files as your banner</li>
                <li>Set your handle to <span className="text-yellow-400">{pickedHandle}</span> (or any free handle)</li>
                <li>Write your About section and hit Publish</li>
              </ol>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}


function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.max(0, (Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
}

/**
 * YourChannelsGallery — renders every channel kit the current user has
 * created (newest first). Shown above the wizard on the Topic step so
 * users can find / re-open any kit they've already built (including
 * ones created from a Saved Niche).
 *
 * Three states per card:
 *   • running   — animated yellow ring + status pill
 *   • complete  — banner + avatar thumbnail + ↓ Download
 *   • failed    — red border + retry hint
 */
function YourChannelsGallery({ items, loading, onDelete, onRegenerate, onSendToYouTube, onEdited }) {
  if (loading) {
    return (
      <div
        data-testid="cc-gallery-loading"
        className="mb-10 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500"
      >
        [recon] loading your channels<span className="blink">_</span>
      </div>
    );
  }
  if (!items?.length) return null;

  return (
    <section
      data-testid="cc-your-channels"
      className="mb-12 border border-zinc-900 bg-zinc-950/40 rounded-sm p-5"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400">
          // YOUR CHANNELS
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          newest first · max 60
        </span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((c) => (
          <ChannelCreationCard
            key={c.job_id}
            c={c}
            onDelete={onDelete}
            onRegenerate={onRegenerate}
            onSendToYouTube={onSendToYouTube}
            onEdited={onEdited}
          />
        ))}
      </div>
    </section>
  );
}

function ChannelCreationCard({ c, onDelete, onRegenerate, onSendToYouTube, onEdited }) {
  const navigate = useNavigate();
  // Defensive image lookup: kits stored under different filename
  // conventions over time, and some older jobs lost their on-disk
  // assets during pod redeploys. Try a few sensible fallbacks, then
  // mark the banner missing if all of them fail to load.
  const bannerCandidates = [
    c.images?.["banner_1.png"],
    c.images?.["banner_2.png"],
    c.images?.["banner.png"],
  ].filter(Boolean);
  const avatar = c.images?.["avatar.png"] || c.images?.["avatar.jpg"];

  const [bannerIdx, setBannerIdx] = useState(0);
  const [bannerBroken, setBannerBroken] = useState(false);
  const banner = bannerCandidates[bannerIdx];

  const isComplete = c.status === "complete";
  const isFailed = c.status === "failed";
  const isCancelled = c.status === "cancelled";
  const isRunning = c.status === "running";

  const borderClass = isFailed
    ? "border-red-500/40"
    : isCancelled
    ? "border-zinc-800"
    : isRunning
    ? "border-yellow-400/40"
    : "border-zinc-800 hover:border-yellow-400/40";

  // Card-wide click handler — opens the kit detail page. Per-button
  // handlers call stopPropagation so Download / Storyboard / Delete
  // don't also navigate to the detail.
  const openDetail = () => navigate(`/create-channel/kit/${c.job_id}`);

  return (
    <div
      data-testid={`cc-creation-card-${c.job_id}`}
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); } }}
      className={`border ${borderClass} rounded-sm bg-black overflow-hidden flex flex-col transition-colors cursor-pointer hover:bg-zinc-950/60`}
    >
      {/* Banner / status hero */}
      <div className="relative aspect-[2048/1152] bg-zinc-900 overflow-hidden">
        {banner && !bannerBroken ? (
          <img
            src={`${API_BASE}${banner}`}
            alt={c.name}
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => {
              // Try the next candidate before giving up.
              if (bannerIdx + 1 < bannerCandidates.length) {
                setBannerIdx(bannerIdx + 1);
              } else {
                setBannerBroken(true);
              }
            }}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 px-3 text-center">
            {isRunning ? (
              <>
                <span className="animate-pulse text-yellow-400">◌ rendering…</span>
              </>
            ) : isFailed ? (
              <span className="text-red-400">failed</span>
            ) : (
              <>
                <span>banner unavailable</span>
                {/* Show the actual reason if the backend recorded one
                    (e.g. "gemini returned no image"). Otherwise fall
                    back to the generic "asset purged" — true when the
                    bytes were lost to a prod redeploy. */}
                <span className="text-zinc-700 normal-case tracking-normal text-[10px] font-sans max-w-[80%]">
                  {c.asset_errors?.["banner_1.png"]
                    || c.asset_errors?.["banner_2.png"]
                    || "asset purged"}
                </span>
                {onRegenerate && (
                  <button
                    type="button"
                    data-testid={`cc-card-regenerate-inline-${c.job_id}`}
                    onClick={(e) => { e.stopPropagation(); onRegenerate(c); }}
                    className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 px-3 py-1.5 rounded-sm transition-colors"
                  >
                    ↻ Regenerate
                  </button>
                )}
              </>
            )}
          </div>
        )}
        <div className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] bg-black/80 border border-zinc-700 px-2 py-0.5 rounded-sm text-zinc-300">
          {c.status?.toUpperCase() || "—"}
        </div>
        {c.source_niche && (
          <div
            data-testid={`cc-card-source-${c.job_id}`}
            className="absolute top-2 right-12 font-mono text-[9px] uppercase tracking-[0.25em] bg-yellow-400/90 text-black border border-yellow-300 px-2 py-0.5 rounded-sm"
            title={`Created from saved niche: ${c.source_niche}`}
          >
            FROM {c.source_niche.slice(0, 22).toUpperCase()}
          </div>
        )}
        {/* Delete control — top-right corner, with a tooltip + confirm. */}
        <button
          type="button"
          data-testid={`cc-card-delete-${c.job_id}`}
          onClick={(e) => { e.stopPropagation(); onDelete?.(c); }}
          title="Delete this kit from your channels"
          className="absolute top-2 right-2 font-mono text-xs text-zinc-200 bg-black/80 hover:text-red-400 hover:bg-black border border-zinc-700 hover:border-red-500/60 px-2 py-1 rounded-sm transition-colors"
        >
          ✕
        </button>
      </div>

      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          {avatar ? (
            <img
              src={`${API_BASE}${avatar}`}
              alt="avatar"
              className="w-8 h-8 rounded-full border border-zinc-700 shrink-0"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-zinc-900 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="text-white font-display text-sm font-bold truncate">
                {c.name || "Untitled"}
              </div>
              <button
                type="button"
                data-testid={`cc-card-edit-name-${c.job_id}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  const next = window.prompt("New channel name:", c.name || "");
                  if (!next || next.trim() === (c.name || "").trim()) return;
                  try {
                    await editChannelCreation(c.job_id, { name: next.trim() });
                    toast.success("Name updated");
                    onEdited?.();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Couldn't rename");
                  }
                }}
                title="Rename channel"
                className="shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] text-yellow-400 hover:text-black hover:bg-yellow-400 border border-yellow-400/60 hover:border-yellow-400 px-1.5 py-0.5 rounded-sm transition-colors"
              >
                edit
              </button>
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="font-mono text-[10px] text-yellow-400 truncate">
                {c.handle || "(no handle)"}
              </div>
              <button
                type="button"
                data-testid={`cc-card-edit-handle-${c.job_id}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  const next = window.prompt(
                    "New @handle (letters, numbers, . _ - only, 3-30 chars):",
                    (c.handle || "").replace(/^@/, ""),
                  );
                  if (next == null) return;
                  try {
                    await editChannelCreation(c.job_id, { handle: next });
                    toast.success("Handle updated");
                    onEdited?.();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Couldn't update handle");
                  }
                }}
                title="Edit @handle"
                className="shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] text-yellow-400 hover:text-black hover:bg-yellow-400 border border-yellow-400/60 hover:border-yellow-400 px-1.5 py-0.5 rounded-sm transition-colors"
              >
                edit
              </button>
            </div>
          </div>
        </div>

        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate mb-2">
          {timeAgo(c.completed_at || c.created_at)}
        </div>

        {isComplete && c.description && (
          <div className="mb-3 border border-zinc-800 rounded-sm bg-black/40 p-2">
            <div className="flex items-center justify-between mb-1">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                About-page copy
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`cc-card-copy-description-${c.job_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    try {
                      navigator.clipboard.writeText(c.description || "");
                      toast.success("Description copied");
                    } catch {
                      toast.error("Couldn't copy — highlight and copy manually");
                    }
                  }}
                  className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 hover:text-yellow-400 transition-colors"
                >
                  ⧉ Copy desc
                </button>
                <button
                  type="button"
                  data-testid={`cc-card-copy-starter-kit-${c.job_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const kitText = formatStarterKitText(c);
                    try {
                      navigator.clipboard.writeText(kitText);
                      toast.success("Starter kit copied — paste into YouTube Studio");
                    } catch {
                      toast.error("Couldn't copy — try the download instead");
                    }
                  }}
                  className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400 hover:text-white transition-colors"
                >
                  ⧉ Copy full kit
                </button>
              </div>
            </div>
            <div
              data-testid={`cc-card-description-${c.job_id}`}
              className="text-[11px] text-zinc-300 leading-snug line-clamp-4 whitespace-pre-line"
            >
              {c.description}
            </div>
            {c.tagline && (
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400/80 truncate">
                “{c.tagline}”
              </div>
            )}
          </div>
        )}

        {isFailed && (
          <div className="text-[11px] text-red-300 leading-snug mb-2 line-clamp-2">
            {c.error || "Generation failed."}
          </div>
        )}

        <div className="mt-auto flex items-center gap-2 flex-wrap">
          {isComplete && !bannerBroken && (
            <a
              data-testid={`cc-card-download-${c.job_id}`}
              href={channelGraphicsDownloadUrl(c.job_id)}
              onClick={(e) => e.stopPropagation()}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-3 py-1.5 rounded-sm transition-colors"
            >
              ↓ Download
            </a>
          )}
          {isComplete && (
            <Link
              data-testid={`cc-card-storyboard-${c.job_id}`}
              to="/storyboards"
              onClick={(e) => e.stopPropagation()}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 border border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 px-3 py-1.5 rounded-sm transition-colors"
            >
              ▸ Storyboard
            </Link>
          )}
          {isComplete && (
            <div onClick={(e) => e.stopPropagation()}>
              <SaveKitButton
                kit_key={c.job_id}
                source_type="create_channel"
                source_id={c.source_niche || null}
                name={c.name || "Untitled"}
                niche={c.source_niche || null}
                channel_name={c.name || null}
                data={{
                  channel_names: [{ name: c.name, handle: c.handle }],
                  color_palette: c.color_palette || {},
                  images: c.images || {},
                  topic: c.topic || null,
                  style_hint: c.style_hint || null,
                  description: c.description || null,
                  tagline: c.tagline || null,
                  keywords: c.keywords || [],
                }}
                images={c.images || {}}
                size="sm"
              />
            </div>
          )}
          {isRunning && (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 animate-pulse">
              ◌ rendering…
            </span>
          )}
          {/* Send-to-YouTube button. Only appears for complete kits.
              After a successful apply, we swap in an "Applied" badge
              that links to the live channel URL instead of the button. */}
          {isComplete && !c.youtube_applied_at && onSendToYouTube && (
            <button
              type="button"
              data-testid={`cc-card-send-to-youtube-${c.job_id}`}
              onClick={(e) => { e.stopPropagation(); onSendToYouTube(c); }}
              title="Apply this kit to your YouTube channel (description, keywords, banner)"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 border border-red-400/50 hover:bg-red-400/10 px-3 py-1.5 rounded-sm transition-colors"
            >
              ▸ Send to YouTube
            </button>
          )}
          {isComplete && c.youtube_applied_at && (
            <a
              href={c.youtube_channel_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`cc-card-applied-badge-${c.job_id}`}
              title="Kit was applied to this YouTube channel — click to open"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 px-3 py-1.5 rounded-sm transition-colors"
            >
              ◉ Applied on YouTube
            </a>
          )}
          {/* Regenerate is available for ANY complete or failed kit —
              lets the user re-roll graphics with a different style
              preset / character cast without deleting first. Hidden
              only during an active render. */}
          {onRegenerate && (isComplete || isFailed) && (
            <button
              type="button"
              data-testid={`cc-card-regenerate-${c.job_id}`}
              onClick={(e) => { e.stopPropagation(); onRegenerate(c); }}
              title="Re-roll banners + avatar with a different style or character cast"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 border border-yellow-400/50 hover:bg-yellow-400/10 px-3 py-1.5 rounded-sm transition-colors"
            >
              ↻ Regenerate graphics
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
